// API service for communicating with Supabase Edge Functions
import { InitiateCallRequest, InitiateCallResponse, CallRecord, UserBaseline } from '../types';
import { supabase } from '../contexts/AuthContext';
import config from '../config';

class ApiService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Use Supabase client to make authenticated requests to Edge Functions
    const functionName = endpoint.replace('/functions/v1/', '');
    const body = options.body ? JSON.parse(options.body as string) : undefined;

    const { data, error } = await supabase.functions.invoke(functionName, {
      body,
    });

    if (error) {
      throw new Error(`API request failed: ${error.message}`);
    }

    return data as T;
  }

  // Bulk initiate multiple calls
  async bulkInitiate(entries: Array<{ phoneNumber: string; userBaseline?: UserBaseline; propertyName?: string; propertyUrl?: string }>, batch?: { id?: string; name?: string }): Promise<{ batchId: string; batchName?: string | null; results: Array<{ phoneNumber: string; callId?: string; vapiCallId?: string; error?: string }> }> {
    const payload = { entries, batchId: batch?.id, batchName: batch?.name };
    return this.makeRequest(config.endpoints.bulkInitiate, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Initiate a new verification call
  async initiateCall(phoneNumber: string, userBaseline?: UserBaseline, propertyName?: string, propertyUrl?: string, batch?: { id?: string; name?: string }, language?: 'en' | 'tr'): Promise<InitiateCallResponse> {
    const request: InitiateCallRequest = { phoneNumber } as any;
    const payload: InitiateCallRequest = {
      ...request,
      ...(userBaseline ? { userBaseline } : {}),
      ...(propertyName ? { propertyName } : {}),
      ...(propertyUrl ? { propertyUrl } : {}),
      ...(batch?.id ? { batchId: batch.id } : {}),
      ...(batch?.name ? { batchName: batch.name } : {}),
      ...(language ? { language } : {}),
    };

    return this.makeRequest<InitiateCallResponse>(config.endpoints.initiateCall, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Note: checkCallStatus is actually a VAPI webhook endpoint, not for frontend polling
  // For polling, we should use getCalls() which returns current call statuses
  // This method is kept for potential future use but shouldn't be used for regular polling
  async checkCallStatus(): Promise<{ message: string; checked: number; completed?: number; failed?: number }> {
    console.warn('checkCallStatus is a webhook endpoint and should not be used for frontend polling. Use getCalls() instead.');
    return this.makeRequest<{ message: string; checked: number; completed?: number; failed?: number }>(config.endpoints.checkCallStatus, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Get all calls with status updates
  async getCalls(): Promise<CallRecord[]> {
    const response = await this.makeRequest<{ calls: CallRecord[] }>(config.endpoints.getCalls);
    return response.calls;
  }

  // Get status of a specific call (optional - for future use)
  async getCallStatus(callId: string): Promise<CallRecord> {
    // For now, we'll get all calls and filter, but this could be optimized later
    const calls = await this.getCalls();
    const call = calls.find(c => c.id === callId);
    if (!call) {
      throw new Error(`Call with ID ${callId} not found`);
    }
    return call;
  }

  // Trigger AI analysis for a transcript
  async analyzeTranscript(callId: string, transcript: string): Promise<void> {
    return this.makeRequest<void>(config.endpoints.analyzeTranscript, {
      method: 'POST',
      body: JSON.stringify({ callId, transcript }),
    });
  }

  // Delete a call record
  async deleteCall(callId: string): Promise<{ success: boolean; deletedId: string }> {
    return this.makeRequest<{ success: boolean; deletedId: string }>(config.endpoints.deleteCall, {
      method: 'POST',
      body: JSON.stringify({ callId }),
    });
  }

  // Parse CSV with AI to detect columns
  async parseCsv(csvContent: string, filename?: string): Promise<{
    detected_columns: {
      property_name: string | null;
      property_url: string | null;
      phone_number: string | null;
      address: string | null;
      city: string | null;
      country: string | null;
    };
    halal_columns: {
      halal_all: string | null;
      halal_some: string | null;
      halal_on_request: string | null;
      halal_nearby: string | null;
    };
    alcohol_columns: {
      alcohol_property: string | null;
      alcohol_restaurant: string | null;
      alcohol_room: string | null;
    };
    leisure_columns: {
      modest_swimwear: string | null;
      women_only: string | null;
      private_hire: string | null;
    };
    confidence: number;
    analysis_summary: string;
    all_headers: string[];
    sample_data: Record<string, string>[];
    total_rows: number;
    csv_content: string;
  }> {
    return this.makeRequest(config.endpoints.parseCsv, {
      method: 'POST',
      body: JSON.stringify({ csvContent, filename }),
    });
  }
}

// Export singleton instance
export const api = new ApiService();
export default api;
